import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { STOPS } from '../data/stops';

// Custom labels for each strip
const STRIP_LABELS = [
  "I love you",
  "You rock",
  "My favorite",
  "Lover",
  "My jai jai",
  "<3 <3",
  "my loveeee",
  "i <3 u",
  "<3 <3 <3",
  "You're my fav"
];

export default function FinalReveal() {
  const [stage, setStage] = useState(0);
  const [trailDots, setTrailDots] = useState([]);
  const [highestZ, setHighestZ] = useState(100);
  const [stripPositions, setStripPositions] = useState([]);
  const [dragging, setDragging] = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const trailId = useRef(0);

  // Collect all photo strips from stops that have photos (memoized)
  // Create 2 strips per stop - first 4 photos and last 4 photos
  const photoStrips = useMemo(() => {
    const strips = [];
    let labelIndex = 0;
    STOPS.forEach(stop => {
      if (stop.reward.photos && stop.reward.photos.length > 0) {
        // First strip: photos 0-3
        strips.push({
          name: stop.name,
          label: STRIP_LABELS[labelIndex % STRIP_LABELS.length],
          photos: stop.reward.photos.slice(0, 4),
        });
        labelIndex++;
        // Second strip: photos 4-7 (if available)
        if (stop.reward.photos.length > 4) {
          strips.push({
            name: stop.name,
            label: STRIP_LABELS[labelIndex % STRIP_LABELS.length],
            photos: stop.reward.photos.slice(4, 8),
          });
          labelIndex++;
        }
      }
    });
    return strips;
  }, []);

  // Initialize positions for photo strips - matching the layout in screenshot
  useEffect(() => {
    // Fixed positions for 10 strips - clustered pairs on left and right
    // Nemi strips (indices 8-9) go in the end/bottom group, one on each side
    const fixedPositions = [
      // Left side - top cluster (2 strips)
      { x: -2, y: -5, rotation: -3 },
      { x: 8, y: 0, rotation: 5 },
      // Left side - bottom cluster (2 strips)
      { x: -1, y: 52, rotation: -4 },
      { x: 9, y: 55, rotation: 6 },
      // Right side - top cluster (2 strips)
      { x: 78, y: -8, rotation: 4 },
      { x: 88, y: -3, rotation: -5 },
      // Right side - bottom cluster (2 strips)
      { x: 76, y: 50, rotation: 5 },
      { x: 86, y: 53, rotation: -4 },
      // Nemi strips - end group (one on each side)
      { x: 3, y: 60, rotation: 3 },   // Left side - bottom
      { x: 82, y: 58, rotation: -3 }, // Right side - bottom
    ];

    const positions = photoStrips.map((_, i) => ({
      ...fixedPositions[i % fixedPositions.length],
      z: 50 + i,
    }));
    setStripPositions(positions);
  }, [photoStrips]);

  // Stage progression
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 1000);
    const t2 = setTimeout(() => setStage(2), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Simple smooth cursor trail effect
  const handleMouseMove = useCallback((e) => {
    const newDot = {
      id: trailId.current++,
      x: e.clientX,
      y: e.clientY,
    };

    setTrailDots(prev => [...prev.slice(-12), newDot]); // Keep max 12 dots for smooth trail
  }, []);

  // Handle dragging photo strips
  const handleStripMouseDown = (e, index) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    // Bring to front
    const newZ = highestZ + 1;
    setHighestZ(newZ);
    setStripPositions(prev => prev.map((pos, i) =>
      i === index ? { ...pos, z: newZ } : pos
    ));
    setDragging(index);
  };

  const handleMouseMoveGlobal = useCallback((e) => {
    // Sparkle trail
    handleMouseMove(e);

    // Dragging
    if (dragging !== null) {
      const x = ((e.clientX - dragOffset.current.x) / window.innerWidth) * 100;
      const y = ((e.clientY - dragOffset.current.y) / window.innerHeight) * 100;

      setStripPositions(prev => prev.map((pos, i) =>
        i === dragging ? { ...pos, x, y } : pos
      ));
    }
  }, [dragging, handleMouseMove]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  // Bring strip to front on hover
  const handleStripHover = (index) => {
    if (dragging !== null) return;
    const newZ = highestZ + 1;
    setHighestZ(newZ);
    setStripPositions(prev => prev.map((pos, i) =>
      i === index ? { ...pos, z: newZ } : pos
    ));
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMoveGlobal);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMoveGlobal);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMoveGlobal, handleMouseUp]);

  return (
    <div className="final-reveal">
      {/* Custom cursor dot */}
      <div
        className="custom-cursor-dot"
        style={{
          position: 'fixed',
          left: trailDots.length > 0 ? trailDots[trailDots.length - 1]?.x : -100,
          top: trailDots.length > 0 ? trailDots[trailDots.length - 1]?.y : -100,
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ffd93d, #e8834a)',
          boxShadow: '0 0 15px rgba(255, 217, 61, 0.6)',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 9999,
          transition: 'left 0.05s ease-out, top 0.05s ease-out',
        }}
      />

      {/* Floating photo strips from all stops */}
      {stage >= 2 && stripPositions.length > 0 && photoStrips.map((strip, index) => (
        <div
          key={strip.name}
          className={`floating-strip ${dragging === index ? 'dragging' : ''}`}
          style={{
            left: `${stripPositions[index]?.x || 0}%`,
            top: `${stripPositions[index]?.y || 0}%`,
            transform: `rotate(${stripPositions[index]?.rotation || 0}deg)`,
            zIndex: stripPositions[index]?.z || 50,
          }}
          onMouseDown={(e) => handleStripMouseDown(e, index)}
          onMouseEnter={() => handleStripHover(index)}
        >
          <div className="floating-strip-frame">
            {strip.photos.map((photo, pIdx) => (
              <div key={pIdx} className="floating-strip-photo">
                <img src={photo} alt={`${strip.name} memory ${pIdx + 1}`} />
              </div>
            ))}
            <div className="floating-strip-label">{strip.label}</div>
          </div>
        </div>
      ))}

      <div className={`chest-container stage-${stage}`}>
        {stage < 2 && (
          <div className="treasure-chest">
            <div className="chest-lid">🎁</div>
          </div>
        )}

        {stage >= 2 && (
          <div className="final-message">
            <h1 className="final-title">You did it, lover! ;)</h1>
            <div className="final-divider">✨</div>

            <p className="final-text">
              Every adventure with you is my favorite — so I made us a place to plan a lifetime more.
            </p>
            <p className="final-text">
              Date ideas, recipes, all the spots we love...
            </p>
            <p className="final-text">
              Now planning is easy for both of us!! <br /> (You're welcome. 😏)
            </p>
            <p className="final-text">
              I love you!! SOOOO so so so much!!
            </p>

            <a
              href="/our-playbook"
              className="final-reveal-btn"
              style={{
                display: 'inline-block',
                marginTop: '1.5rem',
                padding: '1rem 2.5rem',
                background: '#c75b39',
                color: 'white',
                borderRadius: '50px',
                textDecoration: 'none',
                fontFamily: "'Caveat', cursive",
                fontSize: '1.6rem',
                boxShadow: '0 4px 15px rgba(199, 91, 57, 0.3)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
            >
              Unlock Your Surprise...
            </a>

            <div className="final-hearts">
              {['💛', '✨', '💛', '✨', '💛', '✨', '💛'].map((h, i) => (
                <span key={i} className="floating-heart" style={{ animationDelay: `${i * 0.3}s` }}>{h}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
